















































































































import Vue from 'vue';
import axios from 'axios';

interface SupportingDocument {
  Title: string;
  S3Name: string;
  Access: string;
  CountryName: string;
  Uploaded: string;
}

interface NewDocumentForm {
  title: string;
  file: File | null;
  access: string;
}

export default Vue.extend({
  name: 'AcidDocumentsAdmin',

  data() {
    return {
      filter: '',
      documents: [] as SupportingDocument[],
      loading: false,
      showAddDocumentModal: false,
      uploading: false,
      newDocument: {
        title: '',
        file: null,
        access: 'Private'
      } as NewDocumentForm
    };
  },

  computed: {
    filterDocuments(): SupportingDocument[] {
      if (!this.filter) return this.documents;
      const query = this.filter.toLowerCase();
      return this.documents.filter(doc => {
        const title = (doc.Title || '').toLowerCase();
        const country = (doc.CountryName || '').toLowerCase();
        return title.includes(query) || country.includes(query);
      });
    },

    canAddDocument(): boolean {
      return Boolean(this.newDocument.title && this.newDocument.file);
    }
  },

  async mounted(): Promise<void> {
    await this.loadDocuments();
  },

  methods: {
    async loadDocuments(): Promise<void> {
      try {
        this.loading = true;
        const response = await axios.post('/api/document/all?type=3');
        this.documents = response.data || [];
      } catch (error) {
        console.error('Error loading ACID documents:', error);
        this.$buefy.toast.open({
          message: 'Failed to load documents',
          type: 'is-danger'
        });
      } finally {
        this.loading = false;
      }
    },

    getAccessClass(access: string): string {
      return access === 'Public' ? 'tag is-success' : 'tag is-warning';
    },

    formatDate(dateString: string): string {
      return new Date(dateString).toLocaleDateString();
    },

    async downloadDocument(s3Name: string, title: string): Promise<void> {
      try {
        const response = await axios.get(`/api/acid/download/${s3Name}`, {
          responseType: 'blob'
        });

        const contentType = response.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const s3Extension = s3Name && s3Name.includes('.')
          ? s3Name.substring(s3Name.lastIndexOf('.'))
          : '';
        const trimmedTitle = (title || '').trim();
        let filename = trimmedTitle;
        if (filename) {
          if (s3Extension && !filename.toLowerCase().endsWith(s3Extension.toLowerCase())) {
            filename += s3Extension;
          }
        } else {
          filename = s3Name;
        }

        link.setAttribute('download', filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading document:', error);
        this.$buefy.toast.open({
          message: 'Failed to download document',
          type: 'is-danger'
        });
      }
    },

    async deleteDocument(s3Name: string): Promise<void> {
      this.$buefy.dialog.confirm({
        title: 'Delete Document',
        message: 'Are you sure you want to delete this document? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          try {
            await axios.post(`/api/document/delete/${s3Name}`);
            await this.loadDocuments();
            this.$buefy.toast.open({
              message: 'Document deleted successfully',
              type: 'is-success'
            });
          } catch (error) {
            console.error('Error deleting document:', error);
            this.$buefy.toast.open({
              message: 'Failed to delete document',
              type: 'is-danger'
            });
          }
        }
      });
    },

    openAddDocumentModal(): void {
      this.newDocument = {
        title: '',
        file: null,
        access: 'Private'
      };
      this.showAddDocumentModal = true;
    },

    closeAddDocumentModal(): void {
      this.showAddDocumentModal = false;
    },

    onFileSelected(file: File): void {
      this.newDocument.file = file;
    },

    async addDocument(): Promise<void> {
      if (!this.canAddDocument) return;

      try {
        this.uploading = true;
        const formData = new FormData();
        formData.append('file', this.newDocument.file!);
        formData.append('title', this.newDocument.title);
        formData.append('access', this.newDocument.access);

        await axios.post('/api/acid/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        this.closeAddDocumentModal();
        await this.loadDocuments();
        this.$buefy.toast.open({
          message: 'Document added successfully',
          type: 'is-success'
        });
      } catch (error) {
        console.error('Error adding document:', error);
        this.$buefy.toast.open({
          message: 'Failed to add document',
          type: 'is-danger'
        });
      } finally {
        this.uploading = false;
      }
    }
  }
});
